import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL, H3 } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/legal-guidelines")({
  head: () => ({
    meta: [
      { title: "Legal Guidelines — banana.cards" },
      { name: "description", content: "Banana Cards' Ethical Use Guidelines and Acceptable Use Policy." },
      { property: "og:title", content: "Legal Guidelines — banana.cards" },
      { property: "og:description", content: "How Banana Cards may be used responsibly, legally, and ethically." },
    ],
    links: alternateLinkList("/legal-guidelines", "en"),
  }),
  component: LegalGuidelinesPage,
});

export function LegalGuidelinesPage() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Legal"
      italic="Guidelines."
      updated="January 2026"
      intro="Our mission is to help individuals, professionals, and businesses connect smarter through NFC business cards, digital profiles, contact management, automation, and AI-powered tools. These guidelines explain how Banana Cards may be used responsibly, legally, and ethically."
      related={[
        { label: "Terms of Service", to: "/terms" },
        { label: "Privacy Policy", to: "/privacy" },
        { label: "Contact us", to: "/contact" },
      ]}
      sections={[
        {
          id: "overview",
          title: "Who Operates Banana Cards",
          body: (
            <>
              <P>Banana Cards is operated globally by Six Studio Creative FZCO for worldwide software, platform, and technology services. In Vietnam, physical card operations, local distribution, customer support, and related commercial activities are managed by Banana Cards Vietnam.</P>
              <P>These Ethical Use Guidelines protect users, customers, partners, and the wider digital environment while ensuring the platform remains safe, trustworthy, and compliant.</P>
            </>
          ),
        },
        {
          id: "approved",
          title: "Approved & Encouraged Use Cases",
          body: (
            <>
              <P>Banana Cards is built to support legitimate personal, professional, and business activities.</P>
              <H3>Professional Networking</H3>
              <P>Sharing contact details quickly and securely through NFC cards, QR codes, digital profiles, and wallet passes.</P>
              <H3>Business Lead Management</H3>
              <P>Collecting, organizing, tagging, and managing leads or contacts through Banana Cards' contact management tools.</P>
              <H3>CRM and Follow-Up Workflows</H3>
              <P>Using reminders, notes, labels, tasks, pipelines, and automations to manage business relationships effectively.</P>
              <H3>Digital Business Profiles</H3>
              <P>Creating professional profiles that showcase contact details, company information, links, services, portfolios, and booking options.</P>
              <H3>Marketing and Communication</H3>
              <P>Sending appropriate business communications to contacts where the user has a lawful basis, consent, or existing business relationship.</P>
              <H3>AI-Assisted Productivity</H3>
              <P>Using Banana Cards' AI tools to draft messages, summarize contact information, enrich records, suggest follow-ups, or support sales workflows.</P>
              <H3>Business Intelligence</H3>
              <P>Reviewing analytics, engagement data, and contact activity to improve networking, sales, and customer relationship strategies.</P>
            </>
          ),
        },
        {
          id: "prohibited",
          title: "Prohibited Activities — Zero-Tolerance",
          body: (
            <>
              <P>Banana Cards must not be used for activities that are illegal, harmful, abusive, deceptive, or unethical. Prohibited activities include, but are not limited to:</P>
              <H3>Spam or Unsolicited Messaging</H3>
              <P>Users must not send mass unsolicited emails, messages, promotions, or automated communications without proper consent or lawful basis.</P>
              <H3>Fraud, Scams, or Misrepresentation</H3>
              <P>Users must not create fake profiles, impersonate another person or company, mislead recipients, or use Banana Cards to support fraudulent activity.</P>
              <H3>Unauthorized Data Collection</H3>
              <P>Users must not collect, import, scrape, or store personal data without permission, lawful basis, or proper authority.</P>
              <H3>Fake Accounts or Fake Business Profiles</H3>
              <P>Users must not create false identities, fake company profiles, misleading digital cards, or deceptive business representations.</P>
              <H3>Abusive Automation</H3>
              <P>Automation features must not be used to harass, manipulate, spam, or overwhelm recipients.</P>
              <H3>Illegal or Restricted Content</H3>
              <P>Users must not use Banana Cards to promote, distribute, or facilitate illegal goods, services, content, or activities.</P>
              <H3>Phishing or Credential Collection</H3>
              <P>Users must not use Banana Cards profiles, links, forms, or messages to trick people into revealing passwords, payment information, identification documents, or other sensitive data.</P>
              <H3>Malware or Harmful Links</H3>
              <P>Users must not add links, files, scripts, or redirects that contain malware, viruses, spyware, harmful code, or unsafe websites.</P>
              <H3>Harassment, Abuse, or Discrimination</H3>
              <P>Users must not use the platform to threaten, harass, intimidate, discriminate against, or abuse any individual or group.</P>
              <H3>Infringement of Third-Party Rights</H3>
              <P>Users must not upload or share content that infringes copyrights, trademarks, privacy rights, personality rights, or other intellectual property rights.</P>
              <H3>Manipulation of Reviews or Engagement</H3>
              <P>Users must not use Banana Cards to generate fake reviews, fake engagement, misleading testimonials, or manipulated business reputation signals.</P>
            </>
          ),
        },
        {
          id: "privacy-consent",
          title: "Data Privacy and Consent",
          body: (
            <>
              <P>Banana Cards users are responsible for ensuring that any personal data they upload, collect, store, or process through the platform is handled lawfully and with appropriate consent.</P>
              <P>This includes contact details, business cards, scanned information, imported contacts, notes, communication history, and AI-enriched data.</P>
              <P>Users must only upload or process personal data where they have the right to do so. Banana Cards does not authorize the collection, storage, or use of personal data obtained illegally, deceptively, or without proper permission.</P>
            </>
          ),
        },
        {
          id: "ai",
          title: "AI Feature Usage",
          body: (
            <>
              <P>Banana Cards may include AI-powered features that help users draft messages, summarize information, scan business cards, enrich contacts, suggest follow-ups, or automate workflow tasks.</P>
              <P>Users remain responsible for reviewing and approving AI-generated content before using it.</P>
              <P>AI tools must not be used to:</P>
              <UL items={[
                "Generate deceptive or misleading communications",
                "Impersonate another person",
                "Send spam or unauthorized outreach",
                "Create fraudulent contracts, proposals, or documents",
                "Process sensitive personal data without permission",
                "Make high-risk legal, financial, medical, or employment decisions without appropriate human review",
              ]} />
              <P>AI-generated suggestions are provided for productivity support and should not be treated as legal, financial, tax, medical, or professional advice.</P>
            </>
          ),
        },
        {
          id: "enforcement",
          title: "Content Blocking and Service Limitations",
          body: (
            <>
              <P>To protect the integrity of the Banana Cards platform, Six Studio Creative FZCO and/or Banana Cards Vietnam may restrict, suspend, remove, or block accounts, profiles, links, content, automations, or activities that appear unsafe, abusive, illegal, or non-compliant.</P>
              <P>This may include content or activity related to spam, fraud, phishing, harmful links, impersonation, unauthorized data collection, or repeated policy violations.</P>
              <P>We may also limit usage where necessary to protect platform performance, user safety, legal compliance, or the rights of third parties.</P>
            </>
          ),
        },
        {
          id: "user-role",
          title: "Your Role in Maintaining a Safe Platform",
          body: (
            <>
              <P>Every Banana Cards user plays a role in keeping the platform safe, ethical, and trustworthy. Users should:</P>
              <UL items={[
                "Share accurate and truthful information",
                "Respect privacy and consent",
                "Communicate professionally",
                "Use automation responsibly",
                "Review AI-generated content before sending",
                "Report misuse, suspicious activity, or policy violations",
              ]} />
              <P>By using Banana Cards, users agree to follow these Ethical Use Guidelines and any applicable terms, privacy policies, and local laws.</P>
            </>
          ),
        },
        {
          id: "report",
          title: "Reporting Misuse",
          body: (
            <>
              <P>If you believe someone is misusing Banana Cards — impersonating another person or company, sending spam, collecting data without permission, or sharing harmful content — you may report the issue to us.</P>
              <P>Reports may be reviewed by Six Studio Creative FZCO for global platform-related matters and/or Banana Cards Vietnam for Vietnam-based operational matters.</P>
              <P>Email <a href="mailto:hello@banana.cards" className="font-bold underline-offset-4 hover:underline">hello@banana.cards</a> with the subject "Report Misuse / Policy Violation".</P>
            </>
          ),
        },
      ]}
    />
  );
}